/* Dedicated engraving page only. Artwork stays in memory and uploads as original File objects. */
(() => {
  'use strict';
  const ACCEPTED = new Map([
    ['jpg', ['image/jpeg']], ['jpeg', ['image/jpeg']], ['png', ['image/png']],
    ['webp', ['image/webp']], ['pdf', ['application/pdf']], ['svg', ['image/svg+xml']]
  ]);
  let turnstileLoader;
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!turnstileLoader) turnstileLoader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => window.turnstile ? resolve(window.turnstile) : reject(new Error('Security check unavailable'));
      script.onerror = () => reject(new Error('Security check unavailable'));
      document.head.append(script);
    });
    return turnstileLoader;
  }
  const sizeLabel = size => size < 1048576 ? `${Math.max(1, Math.ceil(size / 1024))} KB` : `${(size / 1048576).toFixed(1)} MB`;
  const identifier = () => window.crypto.randomUUID();

  function initialise(root) {
    const form = root.querySelector('[data-engraving-form]');
    if (!form || form.dataset.initialised) return;
    form.dataset.initialised = 'true';
    const submit = form.querySelector('[data-submit]');
    const submitLabel = submit.querySelector('span');
    const status = form.querySelector('[data-form-status]');
    const summary = form.querySelector('[data-error-summary]');
    const notice = form.querySelector('[data-configuration-notice]');
    const picker = form.querySelector('.do-file-input');
    const dropzone = form.querySelector('[data-dropzone]');
    const fileList = form.querySelector('[data-file-list]');
    const announcement = form.querySelector('[data-upload-announcement]');
    const reverseToggle = form.querySelector('[data-reverse-toggle]');
    const reverseField = form.querySelector('[data-reverse-field]');
    const reverseInput = reverseField.querySelector('input');
    const maxFiles = Math.min(3, Number(form.dataset.maxFiles) || 3);
    const maxBytes = Math.min(20 * 1048576, Number(form.dataset.maxBytes) || 20 * 1048576);
    const selected = [];
    const thumbs = new Map();
    let endpoint, token = '', widgetId, integrationReady = false, submitting = false, completed = false, needsAssistance = false;
    let requestKey = null, pendingSnapshot = null, dragDepth = 0, disposed = false;

    function updateButton() {
      submit.disabled = !integrationReady || !token || submitting || completed || needsAssistance;
      submitLabel.textContent = submitting ? 'Sending your request…' : needsAssistance ? 'Request needs assistance' : pendingSnapshot ? 'Retry My Engraving Request' : 'Send My Engraving Request';
    }
    function showStatus(message, kind = '', focus = false, heading = '') {
      status.replaceChildren();
      status.className = `do-form-status${kind ? ` is-${kind}` : ''}`;
      if (heading) { const strong = document.createElement('strong'); strong.textContent = heading; status.append(strong); }
      const p = document.createElement('p'); p.textContent = message; status.append(p);
      status.hidden = false;
      if (focus) status.focus();
    }
    function clearErrors() {
      summary.hidden = true;
      summary.replaceChildren();
      form.querySelectorAll('[data-error-for]').forEach(p => { p.hidden = true; p.textContent = ''; });
      form.querySelectorAll('[aria-invalid="true"]').forEach(input => input.removeAttribute('aria-invalid'));
    }
    function showErrors(errors, focus = true) {
      clearErrors();
      const title = document.createElement('strong'); title.textContent = 'Please check your request.';
      const list = document.createElement('ul');
      Object.entries(errors).forEach(([name, message]) => {
        const error = form.querySelector(`[data-error-for="${name}"]`);
        const input = name === 'artwork' ? picker : form.elements.namedItem(name);
        if (error) { error.textContent = message; error.hidden = false; }
        if (input instanceof HTMLElement) input.setAttribute('aria-invalid', 'true');
        const item = document.createElement('li');
        if (input instanceof HTMLElement) {
          const link = document.createElement('a'); link.href = `#${input.id}`; link.textContent = message;
          link.addEventListener('click', event => { event.preventDefault(); input.focus(); }); item.append(link);
        } else item.textContent = message;
        list.append(item);
      });
      summary.append(title, list); summary.hidden = false;
      if (focus) summary.focus();
    }
    function fileProblem(file) {
      const extension = file.name.split('.').pop().toLowerCase();
      if (!ACCEPTED.has(extension)) return `${file.name}: use JPG, JPEG, PNG, WEBP, PDF or SVG.`;
      if (file.type && file.type !== 'application/octet-stream' && !ACCEPTED.get(extension).includes(file.type)) return `${file.name}: the file type does not match its extension.`;
      if (!file.size) return `${file.name}: this file is empty.`;
      if (file.size > maxBytes) return `${file.name}: each file must be ${maxBytes / 1048576} MB or smaller.`;
      return null;
    }
    function releaseThumbnail(file) { const url = thumbs.get(file); if (url) URL.revokeObjectURL(url); thumbs.delete(file); }
    function renderFiles() {
      fileList.replaceChildren();
      selected.forEach(file => {
        const item = document.createElement('li'); item.className = 'do-file-item';
        const extension = file.name.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'webp'].includes(extension)) {
          const image = document.createElement('img'); image.className = 'do-file-thumbnail'; image.alt = '';
          if (!thumbs.has(file)) thumbs.set(file, URL.createObjectURL(file)); image.src = thumbs.get(file);
          image.addEventListener('error', () => { image.remove(); }, { once: true }); item.append(image);
        } else {
          // SVG and PDF are never inserted as markup, embedded or previewed in a browser document.
          const icon = document.createElement('span'); icon.className = 'do-file-placeholder'; icon.textContent = extension.toUpperCase(); icon.setAttribute('aria-hidden', 'true'); item.append(icon);
        }
        const info = document.createElement('span'); info.className = 'do-file-info';
        const name = document.createElement('span'); name.className = 'do-file-name'; name.textContent = file.name;
        const size = document.createElement('span'); size.className = 'do-file-size'; size.textContent = sizeLabel(file.size); info.append(name, size);
        const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'do-remove-file'; remove.textContent = '×'; remove.setAttribute('aria-label', `Remove ${file.name}`); remove.disabled = Boolean(pendingSnapshot) || submitting;
        remove.addEventListener('click', () => {
          if (pendingSnapshot || submitting) return;
          selected.splice(selected.indexOf(file), 1); releaseThumbnail(file); renderFiles();
          announcement.textContent = `${file.name} removed. ${selected.length} of ${maxFiles} files selected.`;
          picker.focus();
        }); item.append(info, remove); fileList.append(item);
      });
    }
    function addFiles(files) {
      if (pendingSnapshot || submitting || completed) return;
      const errors = [];
      Array.from(files).forEach(file => {
        const error = fileProblem(file);
        if (error) { errors.push(error); return; }
        if (selected.some(existing => existing.name === file.name && existing.size === file.size && existing.lastModified === file.lastModified)) return;
        if (selected.length >= maxFiles) { errors.push(`You can upload up to ${maxFiles} files. Remove a file before adding another.`); return; }
        selected.push(file);
      });
      picker.value = ''; renderFiles();
      announcement.textContent = `${selected.length} of ${maxFiles} files selected.`;
      if (errors.length) showErrors({ artwork: errors.join(' ') }, false);
      else { const error = form.querySelector('[data-error-for="artwork"]'); error.hidden = true; picker.removeAttribute('aria-invalid'); summary.hidden = true; }
    }
    picker.addEventListener('change', () => addFiles(picker.files));
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(type => dropzone.addEventListener(type, event => {
      event.preventDefault(); event.stopPropagation();
      if (submitting || pendingSnapshot || completed) return;
      if (type === 'dragenter') { dragDepth++; dropzone.classList.add('is-dragging'); }
      if (type === 'dragover' && event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      if (type === 'dragleave') { dragDepth--; if (dragDepth <= 0) dropzone.classList.remove('is-dragging'); }
      if (type === 'drop') { dragDepth = 0; dropzone.classList.remove('is-dragging'); addFiles(event.dataTransfer.files); }
    }));
    // Prevent a file dropped outside the target from navigating away from this request.
    const preventOutsideDrop = event => { if (event.dataTransfer && Array.from(event.dataTransfer.types).includes('Files')) event.preventDefault(); };
    root.addEventListener('dragover', preventOutsideDrop); root.addEventListener('drop', preventOutsideDrop);
    reverseToggle.addEventListener('change', () => {
      reverseField.hidden = !reverseToggle.checked;
      reverseInput.disabled = !reverseToggle.checked;
      reverseInput.required = reverseToggle.checked;
      reverseToggle.setAttribute('aria-expanded', String(reverseToggle.checked));
      if (reverseToggle.checked) reverseInput.focus();
    });
    function setLocked(locked) {
      Array.from(form.elements).forEach(input => { if (input !== submit && !input.closest('[data-turnstile]')) input.disabled = locked; });
      if (!locked) reverseInput.disabled = !reverseToggle.checked;
      form.setAttribute('aria-busy', String(submitting)); renderFiles();
    }
    function validate() {
      const errors = {};
      if (!form.elements.full_name.value.trim()) errors.full_name = 'Enter your full name.';
      if (!form.elements.address1.value.trim()) errors.address1 = 'Enter your delivery address.';
      if (!form.elements.city.value.trim()) errors.city = 'Enter your town or city.';
      if (!form.elements.country_code.value) errors.country_code = 'Choose your delivery country.';
      if (!form.elements.email.value.trim() || !form.elements.email.validity.valid) errors.email = 'Enter a valid email address.';
      const quantity = Number(form.elements.quantity.value);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 999) errors.quantity = 'Enter a quantity between 1 and 999.';
      if (reverseToggle.checked && !reverseInput.value.trim()) errors.reverse_text = 'Enter the text you would like engraved on the reverse.';
      if (!form.elements.privacy_acknowledged.checked) errors.privacy_acknowledged = 'Please acknowledge the privacy policy and terms.';
      if (!selected.length) errors.artwork = 'Please upload at least one artwork file.';
      const problems = selected.map(fileProblem).filter(Boolean); if (problems.length) errors.artwork = problems.join(' ');
      if (Object.keys(errors).length) { showErrors(errors); return false; }
      return true;
    }
    function snapshot() {
      const fields = {};
      ['full_name', 'email', 'phone', 'address1', 'address2', 'city', 'province', 'zip', 'country_code', 'quantity', 'front_text', 'instructions', 'website'].forEach(name => { fields[name] = form.elements.namedItem(name).value.trim(); });
      fields.reverse_engraving = String(reverseToggle.checked);
      fields.reverse_text = reverseToggle.checked ? reverseInput.value.trim() : '';
      fields.privacy_acknowledged = 'true'; fields.product = 'slate_coaster';
      return { fields, files: [...selected] };
    }
    function resetSecurity() { token = ''; if (widgetId !== undefined && window.turnstile) window.turnstile.reset(widgetId); updateButton(); }
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (!integrationReady || submitting || completed || needsAssistance) return;
      clearErrors();
      if (!pendingSnapshot && !validate()) return;
      if (!token) { showStatus('Please complete the security check before sending your request.', 'error', true); return; }
      if (!pendingSnapshot) { pendingSnapshot = snapshot(); requestKey = identifier(); }
      const payload = new FormData();
      Object.entries(pendingSnapshot.fields).forEach(([name, value]) => payload.append(name, value));
      payload.append('request_id', requestKey); payload.append('turnstile_token', token);
      // The backend validates scalar fields and spam protection before streaming original artwork bytes.
      pendingSnapshot.files.forEach(file => payload.append('artwork', file, file.name));
      submitting = true; setLocked(true); updateButton();
      showStatus('Sending your original artwork securely. Keep this page open until we confirm receipt.');
      const controller = new AbortController(); const timeout = setTimeout(() => controller.abort(), 120000);
      try {
        const response = await fetch(endpoint.href, { method: 'POST', body: payload, headers: { 'Idempotency-Key': requestKey, 'Accept': 'application/json' }, credentials: 'omit', signal: controller.signal });
        const data = await response.json().catch(() => null);
        if (response.ok && data && data.ok === true && typeof data.reference === 'string' && data.reference.length > 0) {
          completed = true; pendingSnapshot = null; requestKey = null;
          form.classList.add('is-complete');
          const emailMessage = data.email_status === 'needs_review' ? `Your acknowledgement email is delayed. Please contact the store and quote ${data.reference} if it does not arrive.` : data.email_status === 'sent' ? 'We have sent your acknowledgement email.' : 'Your acknowledgement email is on its way.';
          showStatus(`Your reference is ${data.reference}. ${emailMessage} We will review your artwork and email your proof and quote. No payment has been taken.`, 'success', true, 'Your engraving request is safely received.');
          selected.forEach(releaseThumbnail); selected.length = 0; form.reset(); fileList.replaceChildren();
          if (widgetId !== undefined && window.turnstile) window.turnstile.remove(widgetId);
        } else if (data && data.ok === false && data.retryable === false && ([403, 405, 409].includes(response.status) || (response.status === 503 && data.code === 'NOT_CONFIGURED'))) {
          // An idempotency conflict or definite integration failure must not create a new request.
          needsAssistance = true;
          showStatus(`${data.message || 'We cannot send this request safely.'} Please contact us using the contact link on this page. Your details and original files are preserved here; keep this page open.`, 'error', true);
        } else if ([400, 413, 422].includes(response.status) && data && data.ok === false) {
          pendingSnapshot = null; requestKey = null;
          showStatus(data.message || 'Please check the request details and artwork, then try again.', 'error', true);
          if (data.field_errors && typeof data.field_errors === 'object') {
            const allowed = ['full_name', 'email', 'address1', 'address2', 'city', 'province', 'zip', 'country_code', 'quantity', 'reverse_text', 'privacy_acknowledged', 'artwork'];
            const errors = Object.fromEntries(Object.entries(data.field_errors).filter(([name, value]) => allowed.includes(name) && typeof value === 'string'));
            if (Object.keys(errors).length) showErrors(errors);
          }
          resetSecurity();
        } else {
          showStatus(response.status === 409 ? 'Your request is still being processed. Please complete the security check and retry shortly. We will use the same request reference to prevent a duplicate.' : 'We could not confirm receipt yet. Your details and original files are preserved on this page. Complete the security check, then retry safely; the same request will not be submitted twice.', 'error', true);
          resetSecurity();
        }
      } catch {
        showStatus('The connection ended before we could confirm receipt. Your details and original files are preserved on this page. Complete the security check and retry safely using the same request. Keep this page open.', 'error', true);
        resetSecurity();
      } finally { clearTimeout(timeout); submitting = false; setLocked(Boolean(pendingSnapshot) || completed); updateButton(); }
    });

    async function connect() {
      try {
        endpoint = new URL(form.dataset.endpoint);
        if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password || !form.dataset.sitekey || !window.crypto?.randomUUID) throw new Error('Integration not configured');
      } catch { updateButton(); return; }
      notice.textContent = 'Loading the secure request form…';
      try {
        const [security, health] = await Promise.all([
          loadTurnstile(),
          fetch(new URL('/health', endpoint), { credentials: 'omit', signal: AbortSignal.timeout(10000) }).then(response => { if (!response.ok) throw new Error('Unavailable'); return response.json(); })
        ]);
        if (disposed) return;
        if (!health.ready || health.max_files < maxFiles || health.max_file_bytes < maxBytes) throw new Error('Integration not ready');
        integrationReady = true; notice.hidden = true;
        widgetId = security.render(form.querySelector('[data-turnstile]'), {
          sitekey: form.dataset.sitekey, action: 'engraving_request', theme: 'light', size: 'flexible',
          callback: value => { token = value; updateButton(); },
          'expired-callback': () => { token = ''; updateButton(); },
          'error-callback': () => { token = ''; updateButton(); showStatus('The security check could not load. Please check your connection and try the check again.', 'error'); }
        }); updateButton();
      } catch {
        if (disposed) return;
        notice.hidden = false; notice.textContent = 'Online requests are temporarily unavailable. Please use the contact link on this page to discuss your engraving.'; integrationReady = false; updateButton();
      }
    }
    connect();
    const cleanup = event => {
      if (event.target !== root.closest('.shopify-section')) return;
      disposed = true; selected.forEach(releaseThumbnail);
      if (widgetId !== undefined && window.turnstile) window.turnstile.remove(widgetId);
      document.removeEventListener('shopify:section:unload', cleanup);
    };
    document.addEventListener('shopify:section:unload', cleanup);
  }
  function initialiseAll(scope = document) { scope.querySelectorAll('[data-engraving-section]').forEach(initialise); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true }); else initialiseAll();
  document.addEventListener('shopify:section:load', event => initialiseAll(event.target));
})();
